import { useEffect, useState } from 'react';
import { weatherApi, type DayWeather } from '../api/weather.api';

export interface TripWeather {
  /** Keyed by `YYYY-MM-DD`. Empty while it loads and when the trip has none. */
  byDate: Record<string, DayWeather>;
  /**
   * True until the request settles. Tells "not here yet" apart from "this trip
   * has no weather" — an empty map means both, and only the first one is worth
   * holding layout space open for.
   */
  loading: boolean;
}

/**
 * Weather for every day of a trip, keyed by `YYYY-MM-DD`. `placesKey` is
 * `tripWeatherKey(trip)`: a new one refetches.
 *
 * Lives in a hook rather than inside ItineraryTab because that file is already
 * 850 lines. Failures are swallowed on purpose: the itinerary must render
 * exactly the same with or without a weather provider (see ADR-0006).
 */
export function useTripWeather(tripId: string, placesKey: string): TripWeather {
  const [byDate, setByDate] = useState<Record<string, DayWeather>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setByDate({});
    setLoading(true);
    weatherApi.get(tripId)
      .then(days => {
        if (!cancelled) setByDate(Object.fromEntries(days.map(d => [d.date, d])));
      })
      .catch(() => { /* no weather is a valid state, not an error to surface */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tripId, placesKey]);

  return { byDate, loading };
}
